import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, ChevronRight, Sparkles, Cpu, Handshake, GitCompare,
  Send, Factory, Activity, Clock, MessageSquare, Zap, TrendingUp,
  Plus, Mail, FileText
} from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import {
  Card, Chip, CompatibilityBadge, StatusDot, Button,
  CompatibilityRing, ReputationBadge, LivePulse, NavBadge
} from "../components/Primitives";
import { quickMaterials } from "../data/mockData";
import { listProducers, listChats, myReplies as fetchMyReplies, getNetworkStats, myRequestsWithCounts, subscribeNotifications } from "../lib/db";

const FLOW = [
  { key: "describe", icon: Search },
  { key: "match", icon: Cpu },
  { key: "compare", icon: GitCompare },
  { key: "contact", icon: Send },
  { key: "confirm", icon: Handshake },
];

export default function Dashboard() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [query, setQuery] = useState("");
  const [topProducers, setTopProducers] = useState([]);
  const [myRecent, setMyRecent] = useState([]);
  const [recentConvs, setRecentConvs] = useState([]);
  const [replies, setReplies] = useState([]);
  const [stats, setStats] = useState({ producers: 0, openRequests: 0, people: 0, organizations: 0, completedCollaborations: 0 });

  const loadAll = React.useCallback(async () => {
    const [prods, ns, mine] = await Promise.all([
      listProducers(),
      getNetworkStats(),
      user?.id ? myRequestsWithCounts(user.id) : Promise.resolve([]),
    ]);
    setTopProducers(prods.slice(0, 3));
    setStats(ns);
    setMyRecent((mine || []).slice(0, 3));
    if (user?.id) {
      const [chats, reps] = await Promise.all([listChats(user.id), fetchMyReplies(user.id)]);
      setRecentConvs(chats.slice(0, 2));
      setReplies(reps.slice(0, 3));
    } else {
      setRecentConvs([]); setReplies([]);
    }
  }, [user?.id]);

  useEffect(() => {
    let alive = true;
    loadAll().catch(() => {});
    const onFocus = () => { if (alive) loadAll().catch(() => {}); };
    window.addEventListener("focus", onFocus);
    const interval = setInterval(() => { if (alive) loadAll().catch(() => {}); }, 60000);
    const unsub = user?.id ? subscribeNotifications(user.id, () => { if (alive) loadAll().catch(() => {}); }) : null;
    return () => { alive = false; window.removeEventListener("focus", onFocus); clearInterval(interval); unsub && unsub(); };
  }, [user?.id, loadAll]);

  const counters = {
    newReplies: replies.length,
    newMessages: recentConvs.length,
    newProfiles: topProducers.length,
  };
  const totalUpdates =
    counters.newReplies + counters.newMessages + counters.newProfiles;

  const handleSearch = () => navigate("/app/new-request");
  const userName = profile?.full_name || user?.email?.split("@")[0] || "You";

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto animate-fade-up" data-testid="finder-page">
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-electric-400">
            {t("finder.kicker")}
          </p>
          <span className="w-1 h-1 rounded-full bg-carbon-500" />
          <LivePulse label="LIVE" />
          {totalUpdates > 0 && (
            <>
              <span className="w-1 h-1 rounded-full bg-carbon-500" />
              <span className="font-mono text-[11px] uppercase tracking-widest text-ink-900">
                {totalUpdates} {t("finder.updatesLabel")}
              </span>
            </>
          )}
        </div>
        <h1 className="text-3xl md:text-5xl font-semibold tracking-tight leading-[1.05]">
          {t("finder.hero")} <span className="text-electric-400">{t("finder.heroAccent")}</span>
        </h1>
        <p className="text-ink-700 mt-3 max-w-2xl text-sm md:text-base leading-relaxed">
          {t("finder.subtitle")}
        </p>
      </div>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-4 mb-8">
        <Card className="p-5 md:p-6 border-electric-500/30 shadow-[0_0_50px_rgba(46,107,255,0.08)]" data-testid="finder-search-card">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-electric-400" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-ink-700">
              R4Pro Finder · {t("finder.startCta")}
            </span>
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-600" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                placeholder={t("finder.searchPlaceholder")}
                data-testid="finder-search-input"
                className="w-full bg-carbon-900 border border-carbon-600 rounded-lg pl-11 pr-3 py-3.5 text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-electric-500 focus:ring-1 focus:ring-electric-500/40"
              />
            </div>
            <Button onClick={handleSearch} className="h-12 px-6" data-testid="finder-start-btn">
              <Plus className="w-4 h-4" />
              {t("finder.startCta")}
            </Button>
          </div>
          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mr-1">
              {t("finder.quickMaterial")}:
            </span>
            {quickMaterials.map((m) => (
              <button key={m} onClick={() => setQuery(m)} data-testid={`quick-mat-${m}`} className="px-2.5 py-1 rounded-md text-[11px] border border-carbon-600 text-ink-800 hover:border-electric-500/50 hover:text-electric-600 transition font-mono">
                {m}
              </button>
            ))}
          </div>
        </Card>

        <Card onClick={() => navigate("/app/my-requests")} className="p-5 cursor-pointer hover:border-electric-500/40 transition group" data-testid="home-my-requests">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-electric-400" />
              <span className="text-sm font-semibold text-ink-900">
                {t("finder.myRequestsTitle")}
              </span>
            </div>
          </div>
          <p className="text-xs text-ink-600 mb-4">{t("finder.myRequestsSub")}</p>
          <div className="space-y-2">
            {myRecent.length === 0 ? (
              <div className="py-2" data-testid="home-my-requests-empty">
                <p className="text-[11px] text-ink-500 mb-2">{t("finder.myRequestsEmpty")}</p>
                <button
                  onClick={(e) => { e.stopPropagation(); navigate("/app/new-request"); }}
                  data-testid="home-my-requests-create"
                  className="inline-flex items-center gap-1 text-xs text-electric-400 hover:text-electric-600"
                >
                  <Plus className="w-3.5 h-3.5" /> {t("finder.startCta")}
                </button>
              </div>
            ) : myRecent.map((r) => (
              <div key={r.id}
                onClick={(e) => { e.stopPropagation(); navigate(`/app/requests/${r.id}`); }}
                data-testid={`home-my-req-${r.id}`}
                className="flex items-center gap-3 p-2 rounded-md bg-carbon-750 border border-carbon-600 cursor-pointer hover:border-electric-500/30">
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-ink-800 truncate">
                    {lang === "it" ? r.title : (r.titleEn || r.title)}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-ink-600 mt-0.5 flex items-center gap-1.5">
                    <span>{t(`requests.status.${r.status}`) !== `requests.status.${r.status}` ? t(`requests.status.${r.status}`) : r.status}</span>
                    <span>·</span>
                    <span>{r.postedAt}</span>
                  </div>
                </div>
                {r.counts?.total > 0 && (
                  <span className="font-mono text-[10px] text-electric-400 shrink-0" data-testid={`home-my-req-count-${r.id}`}>
                    {r.counts.total} {t("finder.proposalsCountLabel")}
                  </span>
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center gap-1 text-xs text-electric-400 group-hover:text-electric-600">
            {t("finder.viewAll")} <ChevronRight className="w-3.5 h-3.5" />
          </div>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold flex items-center gap-2">
                  <Mail className="w-4 h-4 text-electric-400" />
                  {t("finder.newRepliesTitle")}
                </h2>
                <NavBadge count={counters.newReplies} />
              </div>
              <button onClick={() => navigate("/app/notifications")} className="text-xs text-electric-400 flex items-center gap-1 hover:text-electric-600">
                {t("finder.viewFinder")} <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <p className="text-xs text-ink-600 mb-3">{t("finder.newRepliesSub")}</p>
            <Card className="divide-y divide-carbon-600" data-testid="new-replies-card">
              {replies.length === 0 ? (
                <p className="p-4 text-xs text-ink-500">{t("finder.noRepliesYet")}</p>
              ) : replies.map((rep) => (
                <button key={rep.id} onClick={() => navigate("/app/messages")} data-testid={`reply-${rep.id}`}
                  className={`w-full text-left p-4 hover:bg-carbon-700/40 transition ${rep.unread ? "bg-electric-500/[0.04]" : ""}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-lg bg-carbon-750 border border-carbon-600 flex items-center justify-center font-mono text-xs text-ink-800 shrink-0">
                      {rep.from.split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm font-medium text-ink-900 truncate">{rep.from}</span>
                          <ReputationBadge score={rep.reputation} />
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-[10px] font-mono text-ink-600">{rep.when}</span>
                          {rep.unread && <span className="w-2 h-2 rounded-full bg-electric-500 pulse-electric" />}
                        </div>
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-widest text-ink-500 mt-1">
                        on {rep.onRequest}
                      </div>
                      <div className="text-xs text-ink-700 mt-1.5 leading-snug">
                        {lang === "it" ? rep.excerpt : rep.excerptEn}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </Card>
          </section>

          <section>
            <div className="mb-3">
              <h2 className="text-base font-semibold">{t("finder.howItWorks")}</h2>
              <p className="text-xs text-ink-600 mt-0.5">{t("finder.howItWorksSub")}</p>
            </div>
            <Card className="p-5" data-testid="how-it-works-card">
              <ol className="grid sm:grid-cols-5 gap-3">
                {FLOW.map(({ key, icon: Icon }, i) => (
                  <li key={key} className="relative flex flex-col gap-2 p-3 rounded-lg bg-carbon-900/70 border border-carbon-600 hover:border-electric-500/40 transition">
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] text-electric-400/85">0{i + 1}</span>
                      <Icon className="w-3.5 h-3.5 text-electric-400" />
                    </div>
                    <div className="text-sm font-medium text-ink-900">{t(`finder.steps.${key}.t`)}</div>
                    <div className="text-[11px] text-ink-600 leading-snug">{t(`finder.steps.${key}.d`)}</div>
                  </li>
                ))}
              </ol>
            </Card>
          </section>

          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold flex items-center gap-2">
                  <Factory className="w-4 h-4 text-electric-400" />
                  {t("finder.newestPartners")}
                </h2>
                <NavBadge count={counters.newProfiles} />
              </div>
              <button onClick={() => navigate("/app/producers")} data-testid="see-all-producers" className="text-xs text-electric-400 flex items-center gap-1 hover:text-electric-600">
                {t("finder.viewFinder")} <ChevronRight className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">
              {topProducers.length === 0 ? (
                <p className="text-xs text-ink-500 col-span-3">{t("finder.noProducersYet")}</p>
              ) : topProducers.map((p) => (
                <Card key={p.id} onClick={() => navigate(`/app/producers/${p.id}`)} data-testid={`finder-producer-${p.id}`} className="p-4 cursor-pointer hover:border-electric-500/40 transition">
                  <div className="flex items-center justify-between mb-3">
                    <StatusDot status={p.availability} />
                    <ReputationBadge score={p.reputation} />
                  </div>
                  <div className="text-sm font-medium text-ink-900 truncate">{p.name}</div>
                  <div className="text-[11px] text-ink-600 line-clamp-2 h-8 mt-1">{p.positioning}</div>
                  <div className="text-[10px] font-mono text-ink-600 mt-3 flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {p.leadTimeDays}d · {p.location || "—"}
                  </div>
                </Card>
              ))}
            </div>
          </section>
        </div>

        <aside className="space-y-6">
          <section>
            <h2 className="text-base font-semibold flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-electric-400" />
              {t("finder.networkPulse")}
            </h2>
            <Card className="divide-y divide-carbon-600" data-testid="network-pulse">
              {[
                { k: "people", v: `${stats.people}`, icon: Factory },
                { k: "organizations", v: `${stats.organizations}`, icon: Factory },
                { k: "openRequestsCount", v: `${stats.openRequests}`, icon: FileText },
                { k: "completedCollabs", v: `${stats.completedCollaborations}`, icon: Handshake },
              ].map(({ k, v, icon: Icon }) => (
                <div key={k} className="p-4 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-electric-500/10 border border-electric-500/30 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-electric-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-mono uppercase tracking-wider text-ink-600">
                      {t(`finder.${k}`)}
                    </div>
                    <div className="text-lg font-semibold font-mono text-ink-900">{v}</div>
                  </div>
                </div>
              ))}
              {stats.people + stats.organizations + stats.openRequests + stats.completedCollaborations === 0 && (
                <div className="p-4 text-[11px] text-ink-500" data-testid="network-pulse-empty">
                  {t("finder.noDataYet")}
                </div>
              )}
            </Card>
          </section>

          <section>
            <div className="flex items-center gap-2 mb-3">
              <h2 className="text-base font-semibold flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-ink-700" />
                {t("nav.messages")}
              </h2>
              <NavBadge count={counters.newMessages} />
            </div>
            <Card className="divide-y divide-carbon-600">
              {recentConvs.length === 0 ? (
                <p className="p-4 text-xs text-ink-500">{t("finder.noConversationsYet")}</p>
              ) : recentConvs.map((c) => (
                <button key={c.id} onClick={() => navigate("/app/messages")} data-testid={`finder-conv-${c.id}`} className="w-full flex items-center gap-3 p-3 hover:bg-carbon-700/40 transition text-left">
                  <div className="relative w-9 h-9 rounded-full bg-carbon-750 border border-carbon-600 flex items-center justify-center font-mono text-xs">
                    {c.initials}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium truncate">{c.with}</div>
                      <div className="text-[10px] text-ink-600 font-mono">{c.time}</div>
                    </div>
                    <div className="text-xs text-ink-600 truncate">—</div>
                  </div>
                </button>
              ))}
            </Card>
          </section>
        </aside>
      </div>

      <div className="mt-10 pt-6 border-t border-carbon-600 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-ink-600">
        <span className="flex items-center gap-2">
          {userName} · {profile?.location || user?.email || ""}
          {profile?.reputation > 0 && <ReputationBadge score={profile.reputation} />}
        </span>
        <span>{t("brand.version")}</span>
      </div>
    </div>
  );
}
